import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LineChart,
  Line, Legend
} from 'recharts';
import Sidebar from '../Shared/Sidebar';

export default function StudentProgress() {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);

  const [progress, setProgress] = useState({
    academic: 0,
    behaviour: 0,
    health: 0,
    community: 0,
    lifeSkills: 0,
  });

  useEffect(() => {
    fetchStudents();
  }, []);

  async function fetchStudents() {
    try {
      const snap = await getDocs(collection(db, 'students'));
      setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
  }

  function selectStudent(student) {
    setSelected(student);
    setProgress(student.progress || {
      academic: 0,
      behaviour: 0,
      health: 0,
      community: 0,
      lifeSkills: 0,
    });
    setEditing(false);
    setSuccess('');
  }

  async function handleSaveProgress() {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'students', selected.id), {
        progress,
      });
      setSuccess('Progress updated successfully!');
      setEditing(false);
      fetchStudents();
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  const filtered = students.filter(s =>
    `${s.firstName} ${s.lastName} ${s.studentId}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  // Chart data
  const radarData = [
    { subject: 'Academic', value: progress.academic },
    { subject: 'Behaviour', value: progress.behaviour },
    { subject: 'Health', value: progress.health },
    { subject: 'Community', value: progress.community },
    { subject: 'Life Skills', value: progress.lifeSkills },
  ];

  const barData = [
    { name: 'Academic', score: progress.academic, fill: '#14452F' },
    { name: 'Behaviour', score: progress.behaviour, fill: '#F57C00' },
    { name: 'Health', score: progress.health, fill: '#00695C' },
    { name: 'Community', score: progress.community, fill: '#4527A0' },
    { name: 'Life Skills', score: progress.lifeSkills, fill: '#C62828' },
  ];

  // All students chart data
  const allStudentsData = students.slice(0, 8).map(s => ({
    name: `${s.firstName}`,
    Academic: s.progress?.academic || 0,
    Behaviour: s.progress?.behaviour || 0,
    Health: s.progress?.health || 0,
  }));

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              Progress Tracking
            </div>
            <div style={styles.pageSub}>
              Monitor and update student progress
            </div>
          </div>
        </div>

        <div style={styles.grid}>

          {/* LEFT — Student List */}
          <div style={styles.leftPanel}>
            <div style={styles.searchBar}>
              <span></span>
              <input
                style={styles.searchInput}
                placeholder="Search student..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div style={styles.studentList}>
              {filtered.map(s => (
                <div
                  key={s.id}
                  style={{
                    ...styles.studentItem,
                    ...(selected?.id === s.id
                      ? styles.studentItemActive
                      : {})
                  }}
                  onClick={() => selectStudent(s)}
                >
                  <div style={styles.studentAvatar}>
                    {s.photoURL ? (
                      <img
                        src={s.photoURL}
                        alt={s.firstName}
                        style={styles.avatarImg}
                      />
                    ) : (
                      <span>
                        {s.firstName?.[0]}{s.lastName?.[0]}
                      </span>
                    )}
                  </div>
                  <div style={styles.studentInfo}>
                    <div style={styles.studentName}>
                      {s.firstName} {s.lastName}
                    </div>
                    <div style={styles.studentMeta}>
                      Class {s.class} · {s.studentId}
                    </div>
                  </div>
                  <div style={styles.progressDot(
                    s.progress?.academic || 0
                  )} />
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Progress Details */}
          <div style={styles.rightPanel}>
            {!selected ? (
              <div style={styles.noSelection}>
                <div style={styles.noSelectionIcon}></div>
                <div style={styles.noSelectionText}>
                  Select a student to view their progress
                </div>
              </div>
            ) : (
              <>
                {/* Student Header */}
                <div style={styles.studentHeader}>
                  <div style={styles.headerAvatar}>
                    {selected.photoURL ? (
                      <img
                        src={selected.photoURL}
                        alt={selected.firstName}
                        style={styles.headerAvatarImg}
                      />
                    ) : (
                      <span style={styles.headerAvatarText}>
                        {selected.firstName?.[0]}
                        {selected.lastName?.[0]}
                      </span>
                    )}
                  </div>
                  <div style={styles.headerInfo}>
                    <div style={styles.headerName}>
                      {selected.firstName} {selected.lastName}
                    </div>
                    <div style={styles.headerMeta}>
                      {selected.studentId} ·
                      Class {selected.class} ·
                      {selected.school}
                    </div>
                  </div>
                  <button
                    style={styles.editBtn}
                    onClick={() => setEditing(!editing)}
                  >
                    {editing ? 'Cancel' : 'Update'}
                  </button>
                </div>

                {success && (
                  <div style={styles.successMsg}>
                    {success}
                  </div>
                )}

                {/* Progress Sliders */}
                {editing && (
                  <div style={styles.panel}>
                    <div style={styles.sectionTitle}>
                      Update Progress Scores (0-100)
                    </div>
                    {[
                      { key: 'academic', label: 'Academic Performance', color: '#14452F' },
                      { key: 'behaviour', label: 'Behaviour at School', color: '#F57C00' },
                      { key: 'health', label: 'Health & Wellbeing', color: '#00695C' },
                      { key: 'community', label: '🏘️ Community Behaviour', color: '#4527A0' },
                      { key: 'lifeSkills', label: 'Chess & Life Skills', color: '#C62828' },
                    ].map(item => (
                      <div key={item.key} style={styles.sliderItem}>
                        <div style={styles.sliderRow}>
                          <span style={styles.sliderLabel}>
                            {item.label}
                          </span>
                          <span style={{
                            ...styles.sliderValue,
                            color: item.color
                          }}>
                            {progress[item.key]}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={progress[item.key]}
                          onChange={e => setProgress({
                            ...progress,
                            [item.key]: Number(e.target.value)
                          })}
                          style={{
                            ...styles.slider,
                            accentColor: item.color,
                          }}
                        />
                      </div>
                    ))}
                    <button
                      style={styles.saveBtn}
                      onClick={handleSaveProgress}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Progress'}
                    </button>
                  </div>
                )}

                {/* RADAR CHART */}
                <div style={styles.panel}>
                  <div style={styles.sectionTitle}>
                    Progress Overview
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis
                        dataKey="subject"
                        tick={{ fontSize: 11 }}
                      />
                      <Radar
                        name="Progress"
                        dataKey="value"
                        stroke="#14452F"
                        fill="#14452F"
                        fillOpacity={0.3}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                {/* BAR CHART */}
                <div style={styles.panel}>
                  <div style={styles.sectionTitle}>
                    Progress By Area
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={barData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                        {barData.map((entry, index) => (
                          <rect key={index} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

              </>
            )}
          </div>
        </div>

        {/* ALL STUDENTS COMPARISON */}
        <div style={styles.panel}>
          <div style={styles.sectionTitle}>
            All Students Comparison
          </div>
          {students.length === 0 ? (
            <div style={styles.empty}>
              No students yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={allStudentsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="Academic"
                  stroke="#14452F"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="Behaviour"
                  stroke="#F57C00"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="Health"
                  stroke="#00695C"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  leftPanel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    height: 'fit-content',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#F9FAFB',
    borderRadius: '10px',
    padding: '8px 12px',
    marginBottom: '12px',
    border: '1px solid #E5E7EB',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    fontFamily: 'inherit',
    background: 'transparent',
  },
  studentList: {
    maxHeight: '500px',
    overflowY: 'auto',
  },
  studentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px',
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '4px',
    transition: 'all 0.15s',
  },
  studentItemActive: {
    background: 'linear-gradient(135deg, #EAF3EC, #F1F8E9)',
    border: '1px solid #BBD8C4',
  },
  studentAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    color: '#fff',
    flexShrink: 0,
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  studentInfo: { flex: 1 },
  studentName: {
    fontSize: '13px',
    fontWeight: '500',
  },
  studentMeta: {
    fontSize: '11px',
    color: '#6B7280',
  },
  progressDot: (value) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: value >= 70 ? '#14452F' :
                value >= 40 ? '#F57C00' : '#C62828',
    flexShrink: 0,
  }),
  rightPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  noSelection: {
    background: '#fff',
    borderRadius: '16px',
    padding: '60px 20px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  noSelectionIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  noSelectionText: {
    fontSize: '14px',
    color: '#6B7280',
  },
  studentHeader: {
    background: 'linear-gradient(135deg, #0B3524, #175239)',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    color: '#fff',
  },
  headerAvatar: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    border: '2px solid rgba(255,255,255,0.3)',
  },
  headerAvatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  headerAvatarText: {
    fontSize: '18px',
    fontWeight: '600',
  },
  headerInfo: { flex: 1 },
  headerName: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '18px',
    marginBottom: '4px',
  },
  headerMeta: {
    fontSize: '12px',
    opacity: 0.6,
  },
  editBtn: {
    background: 'rgba(245,124,0,0.2)',
    color: '#FFA726',
    border: '1px solid rgba(245,124,0,0.3)',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  successMsg: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
    color: '#14452F',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
  },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  sectionTitle: {
    fontSize: '11px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#6B7280',
    marginBottom: '14px',
    paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6',
    fontWeight: '600',
  },
  sliderItem: {
    marginBottom: '16px',
  },
  sliderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '6px',
  },
  sliderLabel: {
    fontSize: '13px',
    color: '#374151',
  },
  sliderValue: {
    fontSize: '13px',
    fontWeight: '600',
  },
  slider: {
    width: '100%',
    height: '6px',
    borderRadius: '99px',
    cursor: 'pointer',
  },
  saveBtn: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  empty: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    padding: '20px 0',
  },
};